import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Settings, Save, RotateCcw, Eye, Type,
  Image, Zap, Tag, ShoppingBag, Building, Loader2, HelpCircle, Languages,
  FileText, Phone, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  BannerTab, 
  FlashDealsTab, 
  ContactTab, 
  FooterTab, 
  PagesTab, 
  HelpTab,
  BrandsTab,
  CategoriesTab,
  BannersTab,
  OtherSectionsTab,
  ProductOrderingTab
} from '@/components/admin/homepage';
import EnhancedProductOrderingTab from '@/components/admin/homepage/EnhancedProductOrderingTab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

// Active jobs display component
const ActiveJobsDisplay = ({ activeJobs }) => {
  const jobList = Object.values(activeJobs);
  if (jobList.length === 0) return null;
  
  return (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="font-medium text-blue-800">Arka planda çalışan çeviriler</span>
      </div>
      <div className="space-y-2">
        {jobList.map(job => (
          <div key={job.id} className="flex items-center justify-between text-sm">
            <span className="text-blue-700">{job.message}</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-blue-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${job.progress || 0}%` }}
                />
              </div>
              <span className="text-blue-600 font-medium">%{job.progress || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HomepageManagementPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('banner');
  const [hasChanges, setHasChanges] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  
  // Enhanced Product Ordering - state for full product objects
  const [selectedFlashDeals, setSelectedFlashDeals] = useState([]);
  const [selectedRecommended, setSelectedRecommended] = useState([]);
  
  // Static pages state
  const [staticPages, setStaticPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [translatingPage, setTranslatingPage] = useState(false);
  
  // Background job tracking
  const [activeJobs, setActiveJobs] = useState({});
  const jobPollingRef = useRef({});
  
  // Country contact info state
  const [countryContacts, setCountryContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const targetLanguages = ['en', 'de', 'fr', 'es', 'ar', 'ja', 'pt', 'ru', 'zh', 'fa'];

  // Poll for job status
  const pollJobStatus = async (jobId, jobType, onComplete) => {
    try {
      const res = await api.get(`/admin/translation-jobs/${jobId}`);
      const job = res.data;
      
      setActiveJobs(prev => ({ ...prev, [jobType]: job }));
      
      if (job.status === 'completed') {
        toast.success(job.message || 'Çeviri tamamlandı');
        clearInterval(jobPollingRef.current[jobType]);
        delete jobPollingRef.current[jobType];
        setActiveJobs(prev => {
          const newJobs = { ...prev };
          delete newJobs[jobType];
          return newJobs;
        });
        if (onComplete) onComplete();
      } else if (job.status === 'failed') {
        toast.error(job.message || 'Çeviri başarısız oldu');
        clearInterval(jobPollingRef.current[jobType]);
        delete jobPollingRef.current[jobType];
        setActiveJobs(prev => {
          const newJobs = { ...prev };
          delete newJobs[jobType];
          return newJobs;
        });
      }
    } catch (error) {
      console.error('Job status poll error:', error);
    }
  };

  const startJobPolling = (jobId, jobType, onComplete) => {
    if (jobPollingRef.current[jobType]) {
      clearInterval(jobPollingRef.current[jobType]);
    }
    
    pollJobStatus(jobId, jobType, onComplete);
    
    jobPollingRef.current[jobType] = setInterval(() => {
      pollJobStatus(jobId, jobType, onComplete);
    }, 3000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(jobPollingRef.current).forEach(interval => clearInterval(interval));
    };
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchAllProducts();
    fetchStaticPages();
    fetchCountryContacts();
  }, []);

  const fetchCountryContacts = async () => {
    try {
      setContactsLoading(true);
      const res = await api.get('/admin/contact-info');
      setCountryContacts(res.data);
    } catch (error) {
      console.error('Country contacts fetch error:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  const saveCountryContact = async () => {
    if (!editingContact) return;
    try {
      await api.put(`/admin/contact-info/${editingContact.country_code}`, editingContact);
      toast.success('İletişim bilgileri kaydedildi');
      setContactDialogOpen(false);
      setEditingContact(null);
      fetchCountryContacts();
    } catch (error) {
      console.error('Save contact error:', error);
      toast.error('Kaydetme sırasında hata oluştu');
    }
  };

  const fetchStaticPages = async () => {
    try {
      setPagesLoading(true);
      const res = await api.get('/admin/pages');
      setStaticPages(res.data);
    } catch (error) {
      console.error('Static pages fetch error:', error);
    } finally {
      setPagesLoading(false);
    }
  };

  const translateStaticPage = async (slug) => {
    try {
      toast.info('Çeviri arka planda başlatıldı...');
      const res = await api.post(`/admin/pages/${slug}/translate`);
      
      if (res.data.job_id) {
        startJobPolling(res.data.job_id, `page_${slug}`, () => {
          fetchStaticPages();
        });
      } else {
        toast.success(res.data.message);
        fetchStaticPages();
      }
    } catch (error) {
      console.error('Translate page error:', error);
      toast.error('Çeviri başlatılamadı');
    }
  };

  const saveAndTranslatePage = async () => {
    if (!editingPage) return;
    try {
      setSaving(true);
      await api.put(`/admin/pages/${editingPage.slug}`, {
        title: editingPage.title,
        content: editingPage.content,
        meta_description: editingPage.meta_description,
        is_active: editingPage.is_active
      });
      toast.success('Sayfa kaydedildi, çeviri başlatılıyor...');
      
      setPageDialogOpen(false);
      setEditingPage(null);
      
      const res = await api.post(`/admin/pages/${editingPage.slug}/translate`);
      if (res.data.job_id) {
        startJobPolling(res.data.job_id, `page_${editingPage.slug}`, () => {
          fetchStaticPages();
        });
      }
    } catch (error) {
      console.error('Save and translate error:', error);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const saveStaticPage = async () => {
    if (!editingPage) return;
    try {
      setSaving(true);
      await api.put(`/admin/pages/${editingPage.slug}`, {
        title: editingPage.title,
        content: editingPage.content,
        meta_description: editingPage.meta_description,
        is_active: editingPage.is_active
      });
      toast.success('Sayfa güncellendi');
      setPageDialogOpen(false);
      setEditingPage(null);
      fetchStaticPages();
    } catch (error) {
      console.error('Save page error:', error);
      toast.error('Sayfa kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/homepage/settings');
      setSettings(res.data);
      
      // Eski format (ID listesi) - backward compatibility
      if (res.data.flash_deals_products?.length > 0) {
        setSelectedProducts(res.data.flash_deals_products);
        
        // ID'lerden ürün detaylarını çek
        try {
          const productIds = res.data.flash_deals_products;
          const productsRes = await api.get('/admin/products/search', {
            params: { limit: 100 }
          });
          const allProds = productsRes.data.products || [];
          const selectedProds = allProds.filter(p => productIds.includes(p.id));
          setSelectedFlashDeals(selectedProds);
        } catch (e) {
          console.log('Could not fetch product details');
        }
      }
      
      if (res.data.recommended_products?.length > 0) {
        try {
          const productIds = res.data.recommended_products;
          const productsRes = await api.get('/admin/products/search', {
            params: { limit: 100 }
          });
          const allProds = productsRes.data.products || [];
          const selectedProds = allProds.filter(p => productIds.includes(p.id));
          setSelectedRecommended(selectedProds);
        } catch (e) {
          console.log('Could not fetch product details');
        }
      }
      
      setHasChanges(false);
    } catch (error) {
      console.error('Settings fetch error:', error);
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const res = await api.get('/admin/products/search?limit=100');
      setAllProducts(res.data.products || []);
    } catch (error) {
      console.error('Products fetch error:', error);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await api.get(`/admin/products/search?q=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(res.data.products || []);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const translateAllProducts = async () => {
    try {
      toast.info('Ürün çevirisi arka planda başlatılıyor...');
      
      const res = await api.post('/admin/products/translate-all');
      
      if (res.data.job_id) {
        startJobPolling(res.data.job_id, 'products', () => {
          fetchAllProducts();
        });
      } else {
        toast.success(res.data.message || 'Çeviri tamamlandı');
        await fetchAllProducts();
      }
    } catch (error) {
      console.error('Product translation error:', error);
      toast.error('Ürün çevirisi başlatılamadı');
    }
  };

  const translateMissingFaProducts = async () => {
    try {
      toast.info('Eksik Farsça ürün çevirileri başlatılıyor...');
      const res = await api.post('/admin/products/translate-missing', { language: 'fa' });
      toast.success(res.data.message || 'Eksik Farsça çeviri kuyruğa alındı');
    } catch (error) {
      console.error('Missing FA translation error:', error);
      toast.error('Eksik Farsça çevirisi başlatılamadı');
    }
  };

  const translateAndSave = async () => {
    try {
      setSaving(true);
      setTranslating(true);

      // Üst banner alanlarını dillerine çevir ve kaydet
      const bannerKeys = [];
      const bannerValues = [];
      if (String(settings.top_banner_text || '').trim()) {
        bannerKeys.push('text');
        bannerValues.push(settings.top_banner_text.trim());
      }
      if (String(settings.top_banner_subtext || '').trim()) {
        bannerKeys.push('subtext');
        bannerValues.push(settings.top_banner_subtext.trim());
      }
      if (String(settings.top_banner_link_text || '').trim()) {
        bannerKeys.push('link_text');
        bannerValues.push(settings.top_banner_link_text.trim());
      }

      let topBannerTranslations = settings.top_banner_translations || {};

      if (bannerValues.length > 0) {
        try {
          const res = await api.post('/admin/translate', {
            texts: bannerValues,
            source_lang: 'tr',
            target_langs: targetLanguages,
          });
          const translated = res.data?.translations || {};
          topBannerTranslations = {};
          Object.entries(translated).forEach(([lang, list]) => {
            topBannerTranslations[lang] = {};
            bannerKeys.forEach((key, i) => {
              topBannerTranslations[lang][key] = (list && list[i]) || bannerValues[i];
            });
          });
          toast.success(`Üst banner ${Object.keys(topBannerTranslations).length} dile çevrildi`);
        } catch (err) {
          console.error('Translation error:', err);
          toast.warning('Çeviri yapılamadı, ayarlar yine de kaydedilecek');
        }
      }

      const nextSettings = {
        ...settings,
        top_banner_translations: topBannerTranslations,
        flash_deals_products: selectedProducts,
        flash_deals_auto_select: selectedProducts.length === 0,
      };

      await api.put('/admin/homepage/settings', nextSettings);
      setSettings((prev) => ({ ...prev, top_banner_translations: topBannerTranslations }));
      toast.success('Ayarlar kaydedildi');
      setHasChanges(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
      setTranslating(false);
    }
  };

  /** Sadece üst banner metinlerini diğer dillere çevir (kaydedilmemiş değişiklik gerekmez) */
  const translateTopBanner = async () => {
    try {
      setTranslating(true);
      const bannerKeys = [];
      const bannerValues = [];
      if (String(settings.top_banner_text || '').trim()) {
        bannerKeys.push('text');
        bannerValues.push(settings.top_banner_text.trim());
      }
      if (String(settings.top_banner_subtext || '').trim()) {
        bannerKeys.push('subtext');
        bannerValues.push(settings.top_banner_subtext.trim());
      }
      if (String(settings.top_banner_link_text || '').trim()) {
        bannerKeys.push('link_text');
        bannerValues.push(settings.top_banner_link_text.trim());
      }
      if (bannerValues.length === 0) {
        toast.error('Çevrilecek banner metni yok');
        return;
      }

      const res = await api.post('/admin/translate', {
        texts: bannerValues,
        source_lang: 'tr',
        target_langs: targetLanguages,
      });
      const translated = res.data?.translations || {};
      const topBannerTranslations = {};
      Object.entries(translated).forEach(([lang, list]) => {
        topBannerTranslations[lang] = {};
        bannerKeys.forEach((key, i) => {
          topBannerTranslations[lang][key] = (list && list[i]) || bannerValues[i];
        });
      });

      await api.put('/admin/homepage/settings', {
        ...settings,
        top_banner_translations: topBannerTranslations,
      });
      setSettings((prev) => ({ ...prev, top_banner_translations: topBannerTranslations }));
      toast.success(`Üst banner ${Object.keys(topBannerTranslations).length} dile çevrildi ve kaydedildi`);
      setHasChanges(false);
    } catch (error) {
      console.error('Banner translate error:', error);
      toast.error('Banner çevirisi başarısız');
    } finally {
      setTranslating(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Manuel seçilen ürünlerin ID'lerini ve badge bilgilerini al
      const flashDealIds = selectedFlashDeals.map(p => p.id);
      const recommendedIds = selectedRecommended.map(p => p.id);
      
      // Badge ayarlarını oluştur (product_id -> {country_flag, made_in_turkey})
      const productBadges = {};
      [...selectedFlashDeals, ...selectedRecommended].forEach(p => {
        if (p.country_flag || p.made_in_turkey) {
          productBadges[p.id] = {
            country_flag: p.country_flag || null,
            made_in_turkey: p.made_in_turkey || false
          };
        }
      });
      
      await api.put('/admin/homepage/settings', {
        ...settings,
        flash_deals_products: flashDealIds,
        flash_deals_auto_select: flashDealIds.length === 0,
        recommended_products: recommendedIds,
        recommended_auto_select: recommendedIds.length === 0,
        product_badges: productBadges
      });
      toast.success('Ayarlar kaydedildi - Ürünler ve badge\'ler canlıya alındı!');
      setHasChanges(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    try {
      setSaving(true);
      const res = await api.post('/admin/homepage/settings/reset');
      setSettings(res.data.settings);
      toast.success('Ayarlar varsayılana sıfırlandı');
      setHasChanges(false);
      setResetDialog(false);
    } catch (error) {
      toast.error('Sıfırlama hatası');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateCategory = (index, field, value) => {
    const updated = [...settings.categories];
    updated[index] = { ...updated[index], [field]: value };
    updateSetting('categories', updated);
  };

  const updateBrand = (index, field, value) => {
    const updated = [...settings.brands];
    updated[index] = { ...updated[index], [field]: value };
    updateSetting('brands', updated);
  };

  const updateBanner = (index, field, value) => {
    const updated = [...settings.campaign_banners];
    updated[index] = { ...updated[index], [field]: value };
    updateSetting('campaign_banners', updated);
  };

  const addBrand = () => {
    const newBrand = { name: 'Yeni Marka', logo: '🏷️', discount: '%10', enabled: true, made_in_turkey: false };
    updateSetting('brands', [...settings.brands, newBrand]);
  };

  const removeBrand = (index) => {
    const updated = settings.brands.filter((_, i) => i !== index);
    updateSetting('brands', updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="homepage-management">
      <div className="container mx-auto px-4 py-8">
        {/* Active Jobs Banner */}
        <ActiveJobsDisplay activeJobs={activeJobs} />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Settings className="w-6 h-6 text-primary" />
              Ana Sayfa Yönetimi
            </h1>
            <p className="text-muted-foreground mt-1">
              Ana sayfanın görünümünü ve içeriğini düzenleyin
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => window.open('/', '_blank')} className="gap-2">
              <Eye className="w-4 h-4" />
              {t('homepageManagement.preview') || 'Önizle'}
            </Button>
            <Button variant="outline" onClick={() => setResetDialog(true)} className="gap-2">
              <RotateCcw className="w-4 h-4" />
              {t('homepageManagement.reset') || 'Sıfırla'}
            </Button>
            <Button 
              variant="outline" 
              onClick={translateAllProducts} 
              disabled={activeJobs['products']} 
              className="gap-2"
            >
              {activeJobs['products'] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
              {activeJobs['products'] ? `Çeviriliyor %${activeJobs['products'].progress || 0}` : (t('homepageManagement.translateProducts') || 'Ürünleri Çevir')}
            </Button>
            <Button
              variant="outline"
              onClick={translateMissingFaProducts}
              className="gap-2 border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              <Languages className="w-4 h-4" />
              Eksik Farsça Çevir
            </Button>
            <Button 
              variant="secondary" 
              onClick={translateAndSave} 
              disabled={saving || !hasChanges} 
              className="gap-2"
            >
              {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {t('homepageManagement.translateAndSave') || 'Çevir & Kaydet'}
            </Button>
            <Button onClick={saveSettings} disabled={saving || !hasChanges} className="gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('homepageManagement.save') || 'Kaydet'}
            </Button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <Badge variant="outline" className="bg-yellow-100">Kaydedilmemiş değişiklikler</Badge>
            <span className="text-sm text-yellow-700">Değişiklikleri kaydetmeyi unutmayın</span>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="banner" className="gap-2">
              <Type className="w-4 h-4" />
              Üst Banner
            </TabsTrigger>
            <TabsTrigger value="product-ordering" className="gap-2">
              <Package className="w-4 h-4" />
              Ürün Sıralaması
            </TabsTrigger>
            <TabsTrigger value="flash" className="gap-2">
              <Zap className="w-4 h-4" />
              Flaş İndirimler
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <Tag className="w-4 h-4" />
              Kategoriler
            </TabsTrigger>
            <TabsTrigger value="brands" className="gap-2">
              <ShoppingBag className="w-4 h-4" />
              Markalar
            </TabsTrigger>
            <TabsTrigger value="banners" className="gap-2">
              <Image className="w-4 h-4" />
              Kampanya Bannerları
            </TabsTrigger>
            <TabsTrigger value="other" className="gap-2">
              <Building className="w-4 h-4" />
              Diğer Bölümler
            </TabsTrigger>
            <TabsTrigger value="footer" className="gap-2">
              <Settings className="w-4 h-4" />
              Alt Bilgi (Footer)
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2">
              <Phone className="w-4 h-4" />
              İletişim
            </TabsTrigger>
            <TabsTrigger value="pages" className="gap-2">
              <FileText className="w-4 h-4" />
              Sayfalar
            </TabsTrigger>
            <TabsTrigger value="help" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              Yardım
            </TabsTrigger>
          </TabsList>

          {/* Top Banner Tab */}
          <TabsContent value="banner">
            <BannerTab
              settings={settings}
              updateSetting={updateSetting}
              onTranslate={translateTopBanner}
              translating={translating}
            />
          </TabsContent>

          {/* Product Ordering Tab */}
          <TabsContent value="product-ordering">
            <EnhancedProductOrderingTab 
              settings={settings}
              updateSetting={updateSetting}
              selectedFlashDeals={selectedFlashDeals}
              setSelectedFlashDeals={setSelectedFlashDeals}
              selectedRecommended={selectedRecommended}
              setSelectedRecommended={setSelectedRecommended}
              setHasChanges={setHasChanges}
            />
          </TabsContent>

          {/* Flash Deals Tab */}
          <TabsContent value="flash">
            <FlashDealsTab 
              settings={settings}
              updateSetting={updateSetting}
              productSearch={productSearch}
              setProductSearch={setProductSearch}
              searchProducts={searchProducts}
              searchResults={searchResults}
              setSearchResults={setSearchResults}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              allProducts={allProducts}
              setHasChanges={setHasChanges}
            />
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <CategoriesTab 
              settings={settings} 
              updateCategory={updateCategory} 
            />
          </TabsContent>

          {/* Brands Tab */}
          <TabsContent value="brands">
            <BrandsTab 
              settings={settings}
              updateSetting={updateSetting}
              updateBrand={updateBrand}
              addBrand={addBrand}
              removeBrand={removeBrand}
            />
          </TabsContent>

          {/* Campaign Banners Tab */}
          <TabsContent value="banners">
            <BannersTab 
              settings={settings}
              updateSetting={updateSetting}
              updateBanner={updateBanner}
            />
          </TabsContent>

          {/* Other Sections Tab */}
          <TabsContent value="other">
            <OtherSectionsTab 
              settings={settings}
              updateSetting={updateSetting}
            />
          </TabsContent>

          {/* Footer Tab */}
          <TabsContent value="footer">
            <FooterTab 
              settings={settings}
              updateSetting={updateSetting}
              activeJobs={activeJobs}
              startJobPolling={startJobPolling}
              fetchSettings={fetchSettings}
            />
          </TabsContent>

          {/* Contact Info Tab */}
          <TabsContent value="contact">
            <ContactTab
              countryContacts={countryContacts}
              contactsLoading={contactsLoading}
              editingContact={editingContact}
              setEditingContact={setEditingContact}
              contactDialogOpen={contactDialogOpen}
              setContactDialogOpen={setContactDialogOpen}
              saveCountryContact={saveCountryContact}
            />
          </TabsContent>

          {/* Static Pages Tab */}
          <TabsContent value="pages">
            <PagesTab 
              staticPages={staticPages}
              pagesLoading={pagesLoading}
              editingPage={editingPage}
              setEditingPage={setEditingPage}
              pageDialogOpen={pageDialogOpen}
              setPageDialogOpen={setPageDialogOpen}
              translateStaticPage={translateStaticPage}
              saveStaticPage={saveStaticPage}
              saveAndTranslatePage={saveAndTranslatePage}
              saving={saving}
              translatingPage={translatingPage}
            />
          </TabsContent>

          {/* Help Tab */}
          <TabsContent value="help">
            <HelpTab />
          </TabsContent>
        </Tabs>

        {/* Reset Confirmation Dialog */}
        <AlertDialog open={resetDialog} onOpenChange={setResetDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Ayarları Sıfırla</AlertDialogTitle>
              <AlertDialogDescription>
                Tüm ana sayfa ayarları varsayılan değerlere döndürülecek. Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={resetSettings} className="bg-red-600 hover:bg-red-700">
                Sıfırla
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default HomepageManagementPage;
